import { Component, OnInit, ViewEncapsulation, Input } from "@angular/core";
import { faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import { UntypedFormControl } from "@angular/forms";
import { debounceTime } from "rxjs/operators";
import { CompsDataService } from "../comps-data.service";
import { AppService } from "src/app/app.service";
import { VAIconRegistryService, search } from "@va/core-library/icons";

@Component({
  selector: "app-share-comps-table",
  templateUrl: "./share-comps-table.component.html",
  styleUrls: ["./share-comps-table.component.scss"],
  encapsulation: ViewEncapsulation.None,
  host: { "(document:keydown)": "handleKeyboardEvent($event)" },
})
export class ShareCompsTableComponent implements OnInit {
  @Input() tabId: number;
  @Input() componentType: string;
  @Input() is_cc: boolean = false;
  listOfTableData: Array<any> = [];
  faExclamationCircle = faExclamationCircle;
  suggestedUserList: Array<any> = [];
  sharedListUnmodified: Array<any> = [];
  allowUserSuggestionError = false;
  disableShareBtn = true;
  userInput: UntypedFormControl = new UntypedFormControl();
  statusColor: any = {
    sent: "#3c8abb",
    pending: "#e6ab3b",
    created: "#188240",
  };
  columnSortState: { [key: string]: "ascend" | "descend" | null } = {};
  constructor(
    private dataService: CompsDataService,
    private appService: AppService,
    private iconRegistry: VAIconRegistryService,
  ) {
    this.iconRegistry.registerIcons([search]);
  }

  ngOnInit() {
    this.userInput.valueChanges.subscribe(() => {
      this.disableShareBtn = true;
    });

    this.userInput.valueChanges
      .pipe(debounceTime(500))
      .subscribe((text: string) => {
        if (text.length > 2) {
          //searching after 3 characters
          //prevent error from displaying untill the userSuggestions have been fetched and populated
          this.allowUserSuggestionError = false;
        }
        this.onTextChange(text);
      });

    this.fetchCompsShareHistory();
  }

  onTextChange = (text: string) => {
    if (text.trim().length < 3) {
      this.suggestedUserList = []; //clear all cached/existing userSuggestions
      return; //return before suggestions from server
    }

    this.dataService
      .getUserSuggestions(encodeURIComponent(text))
      .subscribe((data) => {
        this.suggestedUserList = data["data"];
        //allow user suggestion error
        this.allowUserSuggestionError = true;
      });
  };

  onSuggestionSelected = () => {
    this.allowUserSuggestionError = false;
    this.suggestedUserList = []; //clear userSuggestions -- stop showing dropdown
    this.disableShareBtn = false; //enable share button
  };

  handleKeyboardEvent = (event: KeyboardEvent) => {
    if (event.keyCode === 13 && this.suggestedUserList.length > 0) {
      this.onSuggestionSelected();
    }
  };

  shareComps(searchInput: HTMLInputElement) {
    const recipientEmail = this.userInput.value;
    this.dataService
      .shareComps(this.tabId, this.userInput.value, this.is_cc)
      .subscribe((res) => {
        if (res.status.toLowerCase() === "success") {
          this.fetchCompsShareHistory();
          this.appService.logUserAction(
            {},
            "",
            "",
            "COMPS_SHARE",
            { tab_id: this.tabId, recipient_email_id: recipientEmail },
            "",
            "COMPS_PAGE",
          );
        }
      });
    searchInput.focus();
    this.disableShareBtn = true;
    this.userInput.setValue("");
  }

  onSortChange = (sortChangeEvent: {
    key: string;
    value: "ascend" | "descend" | null;
  }) => {
    this.columnSortState.company = null;
    this.columnSortState.recipient = null;
    this.columnSortState.email = null;
    this.columnSortState.status = null;
    this.columnSortState.date = null;
    this.columnSortState[sortChangeEvent.key] = sortChangeEvent.value;

    if (sortChangeEvent.value === null) {
      this.listOfTableData = [...this.sharedListUnmodified];
      return;
    }

    switch (sortChangeEvent.key) {
      case "company":
        this.sortByCompany();
        break;
      case "recipient":
        this.sortByReceiverName();
        break;
      case "status":
        this.sortByStatus();
        break;
      case "email":
        this.sortByEmail();
        break;

      case "time":
        this.sortByTime();
        break;

      default:
        this.listOfTableData = [...this.sharedListUnmodified];
    }
  };

  sortByReceiverName = () => {
    const isDescend =
      this.columnSortState.recipient === "descend" ? true : false;
    const sortedList = [...this.listOfTableData];
    this.appService.basicSort(
      sortedList,
      ["recipient"],
      isDescend,
      (receiverName: string) => {
        return receiverName.toLowerCase();
      },
    );
    this.listOfTableData = sortedList;
  };

  sortByCompany = () => {
    const isDescend = this.columnSortState.company === "descend" ? true : false;
    const sortedList = [...this.listOfTableData];
    this.appService.basicSort(
      sortedList,
      ["company"],
      isDescend,
      (company: string) => {
        return company.toLowerCase();
      },
    );
    this.listOfTableData = sortedList;
  };

  sortByStatus = () => {
    const isDescend = this.columnSortState.status === "descend" ? true : false;
    const sortedList = [...this.listOfTableData];
    this.appService.basicSort(
      sortedList,
      ["status"],
      isDescend,
      (status: string) => {
        return status.toLowerCase();
      },
    );
    this.listOfTableData = sortedList;
  };

  sortByEmail = () => {
    const isDescend = this.columnSortState.email === "descend" ? true : false;
    const sortedList = [...this.listOfTableData];
    this.appService.basicSort(sortedList, ["email"], isDescend);
    this.listOfTableData = sortedList;
  };

  sortByTime = () => {
    const isDescend = this.columnSortState.time === "descend" ? true : false;
    const sortedList = [...this.listOfTableData];
    const transform = (time: string | null) => {
      if (time === null) {
        return Number.MAX_VALUE;
      }
      const dateToSort = time.split("-");
      return Number(`${dateToSort[0]}${dateToSort[1]}${dateToSort[2]}`);
    };
    this.appService.basicSort(sortedList, ["time"], isDescend, transform);
    this.listOfTableData = sortedList;
  };

  fetchCompsShareHistory = () => {
    this.dataService.getSharedCompsHistory(this.tabId).subscribe((data) => {
      this.listOfTableData = data["result"];
      this.sharedListUnmodified = [...this.listOfTableData];
      // //sort data on defaut sorting priority -- sort by date -> sort alphabetically within date
      this.sortByReceiverName();
      this.sortByTime();
    });
  };
}
